import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import WebApp from '@twa-dev/sdk';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import useReferralStore from '../stores/referralStore';
import CurrencyIcon from './CurrencyIcon';
import ReactPixel from 'react-facebook-pixel';
import { FaUser, FaUsers, FaUserSlash, FaTrophy } from 'react-icons/fa';
import useUserStore from '../stores/userStore';
import { apiService } from '../services/api';
import numeral from 'numeral';
import { useNavigate, useLocation } from 'react-router-dom';
import useChallengeStore from '../stores/challengeStore';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const Skeleton = styled.div`
	background: rgba(200,200,200,0.15);
	border-radius: 8px;
	min-height: 24px;
	min-width: 40px;
	width: ${props => props.width || '100%'};
	height: ${props => props.height || '24px'};
	margin: 0 auto;
	animation: skeleton-loading 1.2s infinite linear;
	@keyframes skeleton-loading {
		0% { opacity: 0.5; }
		50% { opacity: 1; }
		100% { opacity: 0.5; }
	}
`;

const Container = styled.div`
	min-height: 100vh;
	background: ${props => props.theme.background};
	padding: 0 16px;
	box-sizing: border-box;

	/* Скрываем скроллбар, но оставляем функциональность */
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
`;

const ReferralSection = styled.div`
	margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.text.primary};
  text-align: center;
	margin: 0 0 16px 0;
`;

const ShareButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	padding: 12px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	border-radius: 12px;
	font-size: 16px;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.9;
	}

	svg {
		font-size: 18px;
	}
`;

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin-bottom: 20px;
`;

const StatCard = styled.div`
	background: ${props => props.theme.card.overlay};
	border-radius: 12px;
	padding: 16px;
	text-align: center;
	position: relative;
	cursor: help;

	&:hover::after {
		content: attr(data-tooltip);
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		background: rgba(0, 0, 0, 0.8);
		color: white;
		padding: 8px 12px;
		border-radius: 8px;
		font-size: 12px;
		white-space: nowrap;
		z-index: 1000;
		margin-bottom: 8px;
	}
`;

const StatValue = styled.div`
	font-size: 24px;
	font-weight: bold;
	color: ${props => props.theme.text.primary};
	margin-bottom: 4px;
`;

const StatLabel = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
`;

const ReferralsList = styled.div`
	margin-top: 24px;
	padding-bottom: 20px;
`;

const ReferralItem = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background: ${({ theme }) => theme.type === 'light' ? '#fff' : '#18181b'};
	border-radius: 16px;
	margin-bottom: 8px;
	box-shadow: 0 2px 8px rgba(0,0,0,0.05);

	&:last-child {
		margin-bottom: 0;
	}
`;

const Avatar = styled.img`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
`;

const UserInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

const Username = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const UsernameText = styled.span`
	display: block;
	width: 100%;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const JoinDate = styled.div`
	font-size: 12px;
	color: ${props => props.theme.text.secondary};
`;

const TokensEarned = styled.div`
	font-size: 14px;
	color: ${props => props.theme.primary};
	font-weight: 500;
	margin-top: 4px;
`;

const ReferralCodeSection = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	padding: 20px;
	margin-bottom: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ReferralCodeTitle = styled.h3`
	margin: 0 0 16px 0;
	font-size: 18px;
	color: ${props => props.theme.text.primary};
`;

const BotInfo = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
	margin-bottom: 12px;
`;

const ReferralCodeContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	background: ${props => props.theme.card.overlay};
	border-radius: 12px;
	padding: 12px;
`;

const ReferralCode = styled.div`
	flex: 1;
	font-size: 16px;
	font-weight: 500;
	color: ${props => props.theme.text.primary};
	letter-spacing: 1px;
`;

const CopyButton = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	border-radius: 8px;
	padding: 8px 16px;
	font-size: 14px;
	cursor: pointer;
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.9;
	}

	svg {
		font-size: 16px;
	}
`;

const HeaderBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-bottom: 24px;
`;

const HeaderText = styled.div`
	font-size: 16px;
	color: ${props => props.theme.text.primary};
	text-align: center;
	margin-top: 4px;
`;

const PaginationContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 12px;
	margin-top: 16px;
	margin-bottom: 100px;
`;

const PageButton = styled.button`
	background: ${props => props.theme.card.background};
	color: ${props => props.theme.text.primary};
	border: 1px solid ${props => props.theme.border};
	border-radius: 8px;
	padding: 6px 14px;
	font-size: 15px;
	cursor: pointer;
	transition: background 0.2s;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const FixedInviteButton = styled.button`
	position: fixed;
	left: 16px;
	right: 16px;
	bottom: calc(var(--tg-safe-area-inset-bottom, 0px) + 82px);
	height: 50px;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 18px 16px 18px 16px;
	border-radius: 12px;
	background: ${props => props.theme.primary};
	color: white;
	border: none;
	font-size: 18px;
	font-weight: 600;
	cursor: pointer;
	box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
	transition: opacity 0.2s;

	&:hover {
		opacity: 0.93;
	}

	svg {
		font-size: 20px;
	}
`;

const DefaultAvatar = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background: #2563eb;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 18px;
	font-weight: 600;
`;

const BonusBlock = styled.div`
	flex-shrink: 0;
	margin-left: 8px;
	font-weight: 700;
	color: ${({ theme }) => theme.type === 'light' ? '#2eaf6c' : '#4ade80'};
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 16px;
	min-width: 70px;
	justify-content: center;
`;

const BottomFade = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 62px; /* высота кнопки + отступ */
  height: 80px;
  pointer-events: none;
  z-index: 999;
  background: linear-gradient(
    to bottom,
    rgba(24,24,27,0) 0%,
    ${({ theme }) => theme.type === 'dark' ? '#18181b' : '#fff'} 100%
  );
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 20px;
  border: none;
  background: ${({ active, theme }) => active ? theme.primary : theme.card.background};
  color: ${({ active, theme }) => active ? '#fff' : theme.text.primary};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  box-shadow: ${({ active }) => active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'};
`;

const ChallengeStats = styled.div`
	background: ${props => props.theme.card.background};
	border-radius: 16px;
	padding: 20px;
	margin-bottom: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ChallengeTitle = styled.h3`
	margin: 0 0 16px 0;
	font-size: 18px;
	color: ${props => props.theme.text.primary};
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ChallengePeriod = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
	margin-bottom: 16px;
`;

const TotalReferrals = styled.div`
	background: ${props => props.theme.card.overlay};
	border-radius: 12px;
	padding: 16px;
	text-align: center;
	margin-bottom: 20px;
`;

const TotalValue = styled.div`
	font-size: 24px;
	font-weight: bold;
	color: ${props => props.theme.primary};
	margin-bottom: 4px;
`;

const TotalLabel = styled.div`
	font-size: 14px;
	color: ${props => props.theme.text.secondary};
`;

const LeaderboardItem = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	background: ${({ theme, isCurrentUser }) =>
		isCurrentUser ? 'rgba(37, 99, 235, 0.1)' : theme.card.overlay};
	border-radius: 12px;
	margin-bottom: 8px;
	position: relative;
	border: ${({ isCurrentUser }) => isCurrentUser ? '2px solid #22c55e' : 'none'};
	box-shadow: ${({ isCurrentUser }) => isCurrentUser ? '0 0 0 2px #22c55e33' : 'none'};

	&:last-child {
		margin-bottom: 0;
	}
`;

const RankBadge = styled.div`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: ${props => {
		if (props.rank === 1) return '#FFD700';
		if (props.rank === 2) return '#C0C0C0';
		if (props.rank === 3) return '#CD7F32';
		return props.theme.primary;
	}};
	color: ${props => props.rank <= 3 ? '#000' : 'white'};
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	font-size: 14px;
	flex-shrink: 0;
`;

const ChallengeUserInfo = styled.div`
	flex: 1;
	min-width: 0;
`;

const ChallengeUsername = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const ChallengeUsernameText = styled.span`
	display: block;
	width: 100%;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	font-weight: 600;
`;

const ReferralsCount = styled.div`
	font-size: 16px;
	color: ${props => props.theme.primary};
	font-weight: 700;
	display: flex;
	align-items: center;
	gap: 4px;
`;

const CurrentUserIndicator = styled.div`
	position: absolute;
	top: 8px;
	right: 8px;
	background: ${props => props.theme.primary};
	color: white;
	font-size: 10px;
	padding: 2px 6px;
	border-radius: 4px;
	font-weight: 600;
`;

const getBotUsername = () => {
	return process.env.REACT_APP_BOT_USERNAME || '';
};

function AdaptiveTooltip({ text, anchorRect, onClose }) {
	if (!anchorRect) return null;
	// Вычисляем позицию: если не помещается сверху — показываем снизу
	const tooltipWidth = 220;
	const padding = 8;
	const windowWidth = window.innerWidth;
	const windowHeight = window.innerHeight;
	let top = anchorRect.top - 40;
	let left = anchorRect.left + anchorRect.width / 2 - tooltipWidth / 2;
	if (top < 0) top = anchorRect.bottom + 8;
	if (left < padding) left = padding;
	if (left + tooltipWidth > windowWidth) left = windowWidth - tooltipWidth - padding;
	return (
		<div
			style={{
				position: 'fixed',
				top,
				left,
				width: tooltipWidth,
				background: 'rgba(0,0,0,0.92)',
				color: '#fff',
				padding: '10px 14px',
				borderRadius: 10,
				fontSize: 14,
				zIndex: 2000,
				boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
				pointerEvents: 'auto',
				textAlign: 'center',
				lineHeight: 1.4,
				wordBreak: 'break-word',
			}}
			onClick={onClose}
		>
			{text}
		</div>
	);
}

const ReferralStatsBlock = ({ stats, filter, setFilter, t }) => {
	const theme = useTheme();
	const inactive = Math.max(0, stats.invitedCount - stats.activeUsers);
	const bg = theme.type === 'light' ? '#fff' : '#18181b';
	const text = theme.type === 'light' ? '#000' : '#fff';
	const divider = theme.type === 'light' ? '#eee' : 'rgba(255,255,255,0.07)';
	return (
		<div style={{
			background: bg,
			borderRadius: 16,
			overflow: 'hidden',
			marginBottom: 24,
			boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
		}}>
			<div
				style={{ display: 'flex', alignItems: 'center', padding: '18px 20px', cursor: 'pointer', background: filter === 'all' ? 'rgba(0,0,0,0.03)' : 'none' }}
				onClick={() => setFilter('all')}
			>
				<FaUser size={20} style={{ marginRight: 14, color: text }} />
				<span style={{ flex: 1, fontSize: 16, color: text }}>{t('friends.invitedLabel')}</span>
				<span style={{ fontWeight: 700, fontSize: 18, color: text }}>{stats.invitedCount}</span>
			</div>
			<div style={{ height: 1, background: divider, margin: '0 16px' }} />
			<div
				style={{ display: 'flex', alignItems: 'center', padding: '18px 20px', cursor: 'pointer', background: filter === 'active' ? 'rgba(0,0,0,0.03)' : 'none' }}
				onClick={() => setFilter('active')}
			>
				<FaUsers size={20} style={{ marginRight: 14, color: text }} />
				<span style={{ flex: 1, fontSize: 16, color: text }}>{t('friends.activeLabel')}</span>
				<span style={{ fontWeight: 700, fontSize: 18, color: text }}>{stats.activeUsers}</span>
			</div>
			<div style={{ height: 1, background: divider, margin: '0 16px' }} />
			<div
				style={{ display: 'flex', alignItems: 'center', padding: '18px 20px', cursor: 'pointer', background: filter === 'inactive' ? 'rgba(0,0,0,0.03)' : 'none' }}
				onClick={() => setFilter('inactive')}
			>
				<FaUserSlash size={20} style={{ marginRight: 14, color: theme.type === 'light' ? '#888' : '#aaa' }} />
				<span style={{ flex: 1, fontSize: 16, color: text }}>{t('friends.inactiveLabel')}</span>
				<span style={{ fontWeight: 700, fontSize: 18, color: text }}>{inactive}</span>
			</div>
		</div>
	);
};

const Referrals = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	console.log('Current language:', i18n.language);
	console.log('Translation test:', t('common.shareReferral'));

	const {
		referralCode,
		stats,
		referrals,
		loading,
		error,
		fetchReferrals,
		pagination,
		setPage,
		lastUpdated
	} = useReferralStore();

	const { challenges, loading: challengesLoading, fetchChallenges, lastUpdated: challengesLastUpdated } = useChallengeStore();

	const [tooltip, setTooltip] = useState(null);
	const [filter, setFilter] = useState('all');
	// Удалено: const [challengeData, setChallengeData] = useState(null);
	// Удалено: const [challengeLoading, setChallengeLoading] = useState(false);
	// Удалено: const [challengeError, setChallengeError] = useState(null);

	// Получаем данные о текущем пользователе из store
	const currentUser = useUserStore?.()?.user;

	// Новое состояние для списка челленджей и выбранного челленджа
	const [selectedChallengeId, setSelectedChallengeId] = useState(null);
	const [challengeWinners, setChallengeWinners] = useState(null);
	const [winnersLoading, setWinnersLoading] = useState(false);
	const [winnersError, setWinnersError] = useState(null);

	// Определяем активный таб по URL
	const isChallengeTab = location.pathname.includes('referrals-challenge');
	const activeTab = isChallengeTab ? 'challenge' : 'referrals';

	// При переключении таба меняем URL
	const handleTabChange = (tab) => {
		if (tab === 'challenge') {
			navigate('/referrals-challenge');
		} else {
			navigate('/referrals');
		}
	};

	// Загрузка списка челленджей при первом открытии вкладки «Челлендж»
	useEffect(() => {
		if (
			activeTab === 'challenge' &&
			!challengesLoading
		) {
			fetchChallenges();
		}
	}, [activeTab, challengesLoading, fetchChallenges]);

	// Загрузка победителей по выбранному челленджу
	useEffect(() => {
		if (activeTab === 'challenge' && selectedChallengeId) {
			setWinnersLoading(true);
			setWinnersError(null);
			apiService.getChallengeWinners(selectedChallengeId)
				.then((data) => {
					setChallengeWinners(data);
				})
				.catch((err) => {
					setWinnersError(err.message || 'Ошибка при загрузке победителей');
				})
				.finally(() => setWinnersLoading(false));
		}
	}, [activeTab, selectedChallengeId]);

	useEffect(() => {
		const now = Date.now();
		const shouldForceUpdate = !lastUpdated || (now - lastUpdated) >= 60000;

		console.log('useEffect triggered - page:', pagination.page, 'shouldForceUpdate:', shouldForceUpdate);
		// Всегда загружаем данные при изменении страницы или принудительном обновлении
		fetchReferrals(pagination.page, shouldForceUpdate);
	}, [pagination.page, lastUpdated]);

	// Удаляю функцию fetchChallengeData и связанный useEffect

	useEffect(() => {
		if (activeTab === 'challenge' && challenges.length > 0 && !selectedChallengeId) {
			setSelectedChallengeId(challenges[0].id);
		}
	}, [activeTab, challenges, selectedChallengeId]);

	const handleShareReferral = () => {
		const botUsername = getBotUsername();
		const refLink = `https://t.me/${botUsername}?startapp=ref_${referralCode || ''}`;
		ReactPixel.trackCustom('InviteFriend');
		if (WebApp.isVersionAtLeast('6.0')) {
			WebApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(refLink)}&text=${encodeURIComponent(t('common.shareReferral'))}`);
		} else {
			navigator.clipboard.writeText(refLink);
			toast.info(t('common.linkCopied'));
		}
	};

	const handleCopyReferralCode = () => {
		const botUsername = getBotUsername();
		if (!botUsername) {
			toast.error(t('friends.botUsernameError'));
			return;
		}
		const refLink = `https://t.me/${botUsername}?startapp=ref_${referralCode || ''}`;
		navigator.clipboard.writeText(refLink);
		toast.success(t('friends.copySuccess'));
	};

	const handlePrevPage = () => {
		console.log('Prev clicked, current page:', pagination.page, 'total pages:', pagination.totalPages);
		if (pagination.page > 1) {
			const newPage = pagination.page - 1;
			console.log('Setting page to:', newPage);
			setPage(newPage);
			fetchReferrals(newPage, false);
		}
	};

	const handleNextPage = () => {
		console.log('Next clicked, current page:', pagination.page, 'total pages:', pagination.totalPages);
		if (pagination.page < pagination.totalPages) {
			const newPage = pagination.page + 1;
			console.log('Setting page to:', newPage);
			setPage(newPage);
			fetchReferrals(newPage, false);
		}
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString(i18n.language, {
			day: 'numeric',
			month: 'short',
			year: 'numeric'
		});
	};

	if (error) {
		return <Container><div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>{error}</div></Container>;
	}

	const filteredReferrals = filter === 'all'
		? referrals
		: filter === 'active'
			? referrals.filter(r => r.firstCaseOpenedAt)
			: referrals.filter(r => !r.firstCaseOpenedAt);

	const PAGE_LIMIT = 10; // если лимит другой, подставьте нужное значение
	// Используем referrals (данные с сервера) для отображения, а не filteredReferrals
	const startIdx = (pagination.page - 1) * PAGE_LIMIT;
	const endIdx = startIdx + PAGE_LIMIT;
	const pageReferrals = referrals.slice(startIdx, endIdx);

	return (
		<Container>
			<ReferralSection>
				<Title>{t('friends.programTitle')}</Title>
			</ReferralSection>

			{/* Табы */}
			<Tabs>
				<TabButton
					active={activeTab === 'referrals'}
					onClick={() => handleTabChange('referrals')}
				>
					<FaUsers />
					{t('friends.yourReferrals')}
				</TabButton>
				<TabButton
					active={activeTab === 'challenge'}
					onClick={() => handleTabChange('challenge')}
				>
					<FaTrophy />
					{t('friends.challenge')}
				</TabButton>
			</Tabs>

			{/* Контент таба "Your Referrals" */}
			{activeTab === 'referrals' && (
				<>
					<ReferralStatsBlock stats={stats} filter={filter} setFilter={setFilter} t={t} />

					<ReferralsList>
						<Title>{t('friends.referralsTitle')}</Title>
						{loading
							? Array.from({ length: 3 }).map((_, i) => (
								<ReferralItem key={i}>
									<Avatar as={Skeleton} width="40px" height="40px" />
									<UserInfo>
										<Username><Skeleton width="120px" height="18px" /></Username>
										<JoinDate><Skeleton width="80px" height="14px" /></JoinDate>
									</UserInfo>
								</ReferralItem>
							))
							: pageReferrals.map((referral) => (
								<ReferralItem key={referral.telegramId}>
									<DefaultAvatar>
										{`${referral.firstName?.[0] || ''}${referral.lastName?.[0] || ''}`.toUpperCase() || '?'}
									</DefaultAvatar>
									<UserInfo>
										<Username>
											<UsernameText>
												{`${(referral.firstName || '').trim()}${referral.lastName ? ' ' + referral.lastName.trim() : ''}`.trim()}
											</UsernameText>
											{referral.firstCaseOpenedAt && (
												<BonusBlock>
													+500
													<CurrencyIcon size={16} />
												</BonusBlock>
											)}
										</Username>
										<JoinDate>
											{t('friends.joined', { date: new Date(referral.joinedAt).toLocaleDateString() })}
											{referral.firstCaseOpenedAt && (
												<> • {t('friends.openedCase', { date: new Date(referral.firstCaseOpenedAt).toLocaleDateString() })}</>
											)}
										</JoinDate>
									</UserInfo>
								</ReferralItem>
							))}

						{!loading && referrals.length === 0 && (
							<div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
								{t('friends.noReferrals')}
							</div>
						)}
					</ReferralsList>

					{pagination.totalPages > 1 && (
						<PaginationContainer>
							<PageButton onClick={handlePrevPage} disabled={pagination.page === 1}>
								{t('common.prev')}
							</PageButton>
							<span>{pagination.page} / {pagination.totalPages}</span>
							<PageButton onClick={handleNextPage} disabled={pagination.page === pagination.totalPages}>
								{t('common.next')}
							</PageButton>
						</PaginationContainer>
					)}
				</>
			)}

			{/* Контент таба "Challenge" */}
			{activeTab === 'challenge' && (
				<>
					{/* Табы с названиями челленджей */}
					{challengesLoading ? (
						<div style={{ textAlign: 'center', padding: 32 }}>{t('common.loading')}</div>
					) : challenges.length === 0 ? (
						<div style={{ textAlign: 'center', color: '#666', padding: 32 }}>{t('friends.noActiveChallenges', 'Сейчас нет активных челленджей')}</div>
					) : (
						<>
							<div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
								{challenges.map((ch) => (
									<button
										key={ch.id}
										onClick={() => setSelectedChallengeId(ch.id)}
										style={{
											padding: '8px 18px',
											borderRadius: 20,
											border: 'none',
											background: selectedChallengeId === ch.id ? '#2563eb' : '#eee',
											color: selectedChallengeId === ch.id ? '#fff' : '#222',
											fontWeight: 600,
											fontSize: 15,
											cursor: 'pointer',
											marginBottom: 4
										}}
									>
										{ch.name}
									</button>
								))}
							</div>

							{/* Статистика и топ победителей по выбранному челленджу */}
							{winnersLoading ? (
								<div style={{ textAlign: 'center', padding: 32 }}>{t('common.loading')}</div>
							) : winnersError ? (
								<div style={{ textAlign: 'center', color: 'red', padding: 32 }}>{winnersError}</div>
							) : challengeWinners ? (
								<>
									{/* Здесь можно использовать существующий ChallengeStats и LeaderboardItem, подставляя challengeWinners */}
									<ChallengeStats style={{ padding: '18px 10px', borderRadius: 10, marginBottom: 14, textAlign: 'center' }}>
										<div>
											<span style={{ color: '#bdbdbd', fontSize: 13, marginBottom: 2, display: 'block' }}>{t('friends.prizePool')}</span>
											<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, color: '#22c55e', fontSize: 26, fontWeight: 800, marginTop: 2 }}>
												<FaTrophy style={{ color: '#facc15', fontSize: 22 }} />
												{numeral(challengeWinners.budget).format('0,0').replace(/,/g, '\u202F')}
												<CurrencyIcon size={22} />
											</div>
										</div>
										<ChallengePeriod style={{ marginTop: 16, color: '#bdbdbd', fontSize: 15 }}>
											{challengeWinners.start && challengeWinners.end ? (
												<>{new Date(challengeWinners.start).toLocaleDateString()} - {new Date(challengeWinners.end).toLocaleDateString()}</>
											) : null}
										</ChallengePeriod>
									</ChallengeStats>

									<ReferralsList>
										<Title>{t('friends.leaderboard')}</Title>
										{challengeWinners.top && challengeWinners.top.length > 0 ? (
											<>
												{/* userPlace сверху, если не в топе */}
												{challengeWinners.userPlace &&
													!challengeWinners.top.some(u => u.user.id === challengeWinners.userPlace.user.id) && (
														<LeaderboardItem key={challengeWinners.userPlace.user.id} isCurrentUser>
															<RankBadge rank={challengeWinners.userPlace.place}>{challengeWinners.userPlace.place}</RankBadge>
															<ChallengeUserInfo>
																<ChallengeUsername>
																	<ChallengeUsernameText>
																		{`${challengeWinners.userPlace.user.first_name || ''} ${challengeWinners.userPlace.user.last_name || ''}`.split('@')[0].trim()} <span style={{ color: '#2563eb', fontWeight: 700, marginLeft: 6 }}>{t('common.you')}</span>
																	</ChallengeUsernameText>
																</ChallengeUsername>
																<ReferralsCount>
																	{challengeWinners.userPlace.metric}
																</ReferralsCount>
															</ChallengeUserInfo>
														</LeaderboardItem>
													)}
												{/* Далее обычный топ */}
												{challengeWinners.top.map((user, index) => (
													<LeaderboardItem key={user.user.id} isCurrentUser={currentUser && user.user.id === currentUser.id}>
														<RankBadge rank={index + 1}>{index + 1}</RankBadge>
														<ChallengeUserInfo>
															<ChallengeUsername>
																<ChallengeUsernameText>
																	{`${user.user.first_name || ''} ${user.user.last_name || ''}`.split('@')[0].trim()}
																	{currentUser && user.user.id === currentUser.id && (
																		<span style={{ color: '#2563eb', fontWeight: 700, marginLeft: 6 }}>{t('common.you')}</span>
																	)}
																</ChallengeUsernameText>
															</ChallengeUsername>
															<ReferralsCount>
																{user.metric}
															</ReferralsCount>
														</ChallengeUserInfo>
														<span style={{ color: '#22c55e', fontWeight: 600, fontSize: 15, minWidth: 60, textAlign: 'right' }}>
															{user.reward ? `+${numeral(user.reward).format('0,0').replace(/,/g, '\u202F')}` : ''}
														</span>
														<CurrencyIcon size={16} />
													</LeaderboardItem>
												))}
											</>
										) : (
											<div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>{t('friends.noChallengeData')}</div>
										)}
									</ReferralsList>

									{/* Пагинация для челленджа - пока убираем, так как нет пагинации для челленджей */}
									{/* {pagination.totalPages > 1 && (
										<PaginationContainer>
											<PageButton onClick={handlePrevPage} disabled={pagination.page === 1}>
												{t('common.prev')}
											</PageButton>
											<span>{pagination.page} / {pagination.totalPages}</span>
											<PageButton onClick={handleNextPage} disabled={pagination.page === pagination.totalPages}>
												{t('common.next')}
											</PageButton>
										</PaginationContainer>
									)} */}
								</>
							) : null}
						</>
					)}
				</>
			)}

			{/* BottomFade только для таба challenge */}
			{activeTab === 'challenge' && <BottomFade />}

			{/* Фиксированная кнопка приглашения */}
			{activeTab === 'referrals' && (
				<FixedInviteButton onClick={handleShareReferral}>
					<Trans
						i18nKey="case.inviteFriendReward"
						components={{ currency: <CurrencyIcon size={16} /> }}
					/>
				</FixedInviteButton>
			)}
		</Container>
	);
};

export default Referrals;